import { zodResolver } from '@hookform/resolvers/zod';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
  UseFormReturn,
} from 'react-hook-form';

import { NatureOfBusinessSchema } from './validators';

import { IUserProfile } from '@Domains/Profile';
import { useProfile } from 'src/Constant/UserProfileContext/UserProfileProvider';
import {
  CorporateInformationFormValues,
  INatureOfBusinessFormValues,
  MarketingIndustryFormValues,
  ProductInformationFormValues,
} from './types';

type NatureOfBusinessContext = UseFormReturn<INatureOfBusinessFormValues>;

type NatureOfBusinessFormSubmission = (
  formUtils: NatureOfBusinessContext
) => SubmitHandler<INatureOfBusinessFormValues>;

type NatureOfBusinessFormProviderType = {
  children: React.ReactNode;
  onSubmit: NatureOfBusinessFormSubmission;
  initialValues?: INatureOfBusinessFormValues;
};

const useNatureOfBusinessForm = useFormContext<INatureOfBusinessFormValues>;

export const defaultFormValues: INatureOfBusinessFormValues = {
  marketingIndustry: {
    type: null,
  } as MarketingIndustryFormValues,
  productInformation: {
    businessType: null,
  } as ProductInformationFormValues,
};

const NatureOfBusinessFormProvider = (
  props: NatureOfBusinessFormProviderType
) => {
  const { user } = useProfile();
  const { children, initialValues, onSubmit } = props;

  const mapInitialCompany = (user: IUserProfile) => {
    return {
      thai: {
        symbolTh: user?.E_COMPANY,
        companyTh: user?.N_COMPANY_THAI,
      },
      english: {
        symbolEn: user?.E_COMPANY,
        companyEn: user?.N_COMPANY_ENG,
      },
    } as CorporateInformationFormValues;
  };

  const formContext = useForm({
    mode: 'onChange',
    defaultValues: initialValues ?? {
      ...defaultFormValues,
      corporateInformation: user ? mapInitialCompany(user) : undefined,
      incomeStructure: {
        productIncomeItems: {},
        productIncomePercentages: {},
        exportIncomeItems: {},
        exportIncomePercentages: {},
        otherIncomeItems: {},
      },
    },
    resolver: zodResolver(NatureOfBusinessSchema),
  });

  return (
    <FormProvider {...formContext}>
      <form
        onSubmit={formContext.handleSubmit(onSubmit(formContext))}
        style={{ width: '100%' }}
        noValidate
      >
        {children}
      </form>
    </FormProvider>
  );
};

export { useNatureOfBusinessForm };
export default NatureOfBusinessFormProvider;
